import { Controller } from "@hotwired/stimulus"

const MINUTE_STEP = 5
const ACTIVE_TOGGLE = "rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-sm cursor-pointer"
const INACTIVE_TOGGLE = "rounded-md px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 cursor-pointer"

export default class extends Controller {
  static targets = [
    "nowButton",
    "scheduleButton",
    "panel",
    "weekdays",
    "grid",
    "monthLabel",
    "input",
    "hourSelect",
    "minuteSelect",
    "prevMonth",
    "nextMonth",
    "summary"
  ]

  static values = { min: String, max: String, locale: { type: String, default: "pt-BR" } }

  connect() {
    this.scheduled = false
    this.selectedDate = this.defaultScheduleDate()
    this.viewDate = this.startOfMonth(this.selectedDate)
    try {
      this.render()
    } catch (error) {
      console.error("whatsapp-broadcast-schedule", error)
    }
    this.hidePanel()
    this.inputTarget.value = ""
    this.syncToggle(false)
  }

  selectNow() {
    this.scheduled = false
    this.inputTarget.value = ""
    this.hidePanel()
    this.syncToggle(false)
  }

  selectSchedule() {
    this.scheduled = true
    if (!this.selectedDate || Number.isNaN(this.selectedDate.getTime())) {
      this.selectedDate = this.defaultScheduleDate()
      this.viewDate = this.startOfMonth(this.selectedDate)
    }
    this.render()
    this.showPanel()
    this.syncHidden()
    this.syncToggle(true)
  }

  prevMonth(event) {
    event.preventDefault()
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1)
    this.render()
  }

  nextMonth(event) {
    event.preventDefault()
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1)
    this.render()
  }

  pickDay(event) {
    event.preventDefault()
    if (event.currentTarget.disabled) return

    const [year, month, day] = event.currentTarget.dataset.date.split("-").map(Number)
    const hours = this.selectedDate && !Number.isNaN(this.selectedDate.getTime()) ? this.selectedDate.getHours() : 9
    const minutes = this.selectedDate && !Number.isNaN(this.selectedDate.getTime()) ? this.selectedDate.getMinutes() : 0
    this.selectedDate = this.clamp(new Date(year, month - 1, day, hours, minutes, 0, 0))
    this.render()
    this.syncHidden()
  }

  timeChanged() {
    if (!this.selectedDate || Number.isNaN(this.selectedDate.getTime())) return

    this.selectedDate = this.clamp(new Date(
      this.selectedDate.getFullYear(),
      this.selectedDate.getMonth(),
      this.selectedDate.getDate(),
      Number(this.hourSelectTarget.value),
      Number(this.minuteSelectTarget.value),
      0,
      0
    ))
    this.syncHidden()
    this.renderSummary()
  }

  showPanel() {
    this.panelTarget.hidden = false
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.style.removeProperty("display")
  }

  hidePanel() {
    this.panelTarget.hidden = true
    this.panelTarget.classList.add("hidden")
    this.panelTarget.style.removeProperty("display")
  }

  syncToggle(scheduling) {
    this.nowButtonTarget.className = scheduling ? INACTIVE_TOGGLE : ACTIVE_TOGGLE
    this.scheduleButtonTarget.className = scheduling ? ACTIVE_TOGGLE : INACTIVE_TOGGLE
    this.nowButtonTarget.setAttribute("aria-pressed", scheduling ? "false" : "true")
    this.scheduleButtonTarget.setAttribute("aria-pressed", scheduling ? "true" : "false")
  }

  render() {
    this.renderMonthLabel()
    this.renderNavState()
    this.renderGrid()
    this.renderTimeSelects()
    this.renderSummary()
  }

  renderMonthLabel() {
    if (Number.isNaN(this.viewDate.getTime())) this.viewDate = this.startOfMonth(new Date())
    const label = this.viewDate.toLocaleDateString(this.localeValue, { month: "long", year: "numeric" })
    this.monthLabelTarget.textContent = label.charAt(0).toUpperCase() + label.slice(1)
  }

  renderNavState() {
    const prevMonth = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1)
    const nextMonth = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1)
    this.prevMonthTarget.disabled = this.endOfMonth(prevMonth) < this.startOfDay(this.minDate())
    this.nextMonthTarget.disabled = nextMonth > this.maxDate()
  }

  renderGrid() {
    const weekdayFormatter = new Intl.DateTimeFormat(this.localeValue, { weekday: "short" })
    const headers = Array.from({ length: 7 }, (_, index) => {
      const day = new Date(2026, 8, 13 + index)
      return weekdayFormatter.format(day).replace(".", "")
    })

    this.weekdaysTarget.innerHTML = headers.map((label) => (
      `<th class="pb-2 text-center text-xs font-medium uppercase tracking-wide text-slate-400">${label}</th>`
    )).join("")

    const first = this.startOfMonth(this.viewDate)
    const leadingBlanks = first.getDay()
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
    const cells = []

    for (let i = 0; i < leadingBlanks; i += 1) {
      cells.push("<td class='h-10'></td>")
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(first.getFullYear(), first.getMonth(), day)
      const iso = this.formatDate(date)
      const disabled = !this.daySelectable(date)
      const selected = this.sameDay(date, this.selectedDate)
      const today = this.sameDay(date, new Date())

      cells.push(
        `<td class="p-0.5 text-center">
          <button type="button" data-date="${iso}" data-action="click->whatsapp-broadcast-schedule#pickDay"
            class="${this.dayClasses({ disabled, selected, today })}" ${disabled ? "disabled" : ""}
            aria-pressed="${selected}">${day}</button>
        </td>`
      )
    }

    while (cells.length % 7 !== 0) cells.push("<td class='h-10'></td>")

    const rows = []
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(`<tr>${cells.slice(i, i + 7).join("")}</tr>`)
    }

    this.gridTarget.innerHTML = rows.join("")
  }

  renderTimeSelects() {
    const selectedHour = this.selectedDate && !Number.isNaN(this.selectedDate.getTime()) ? this.selectedDate.getHours() : 9
    const selectedMinute = this.selectedDate && !Number.isNaN(this.selectedDate.getTime()) ? this.roundMinute(this.selectedDate.getMinutes()) : 0

    this.hourSelectTarget.innerHTML = Array.from({ length: 24 }, (_, hour) => {
      const label = String(hour).padStart(2, "0")
      return `<option value="${hour}" ${hour === selectedHour ? "selected" : ""}>${label}</option>`
    }).join("")

    this.minuteSelectTarget.innerHTML = Array.from({ length: 60 / MINUTE_STEP }, (_, index) => {
      const minute = index * MINUTE_STEP
      const label = String(minute).padStart(2, "0")
      return `<option value="${minute}" ${minute === selectedMinute ? "selected" : ""}>${label}</option>`
    }).join("")
  }

  renderSummary() {
    if (!this.selectedDate || Number.isNaN(this.selectedDate.getTime())) {
      this.summaryTarget.textContent = ""
      return
    }

    this.summaryTarget.textContent = this.selectedDate.toLocaleString(this.localeValue, {
      dateStyle: "long",
      timeStyle: "short"
    })
  }

  syncHidden() {
    this.inputTarget.value = this.scheduled && this.selectedDate && !Number.isNaN(this.selectedDate.getTime())
      ? this.formatDateTime(this.selectedDate)
      : ""
  }

  dayClasses({ disabled, selected, today }) {
    const base = "inline-flex h-9 w-9 items-center justify-center rounded-full text-sm"

    if (disabled) return `${base} cursor-not-allowed text-slate-300`
    if (selected) return `${base} cursor-pointer bg-blue-600 font-medium text-white`
    if (today) return `${base} cursor-pointer font-medium text-blue-700 ring-1 ring-blue-600 hover:bg-white`

    return `${base} cursor-pointer text-slate-700 hover:bg-white`
  }

  defaultScheduleDate() {
    const min = this.minDate()
    const tomorrowMorning = new Date(min.getFullYear(), min.getMonth(), min.getDate() + 1, 9, 0, 0, 0)
    if (!Number.isNaN(tomorrowMorning.getTime()) && tomorrowMorning >= min && tomorrowMorning <= this.maxDate()) {
      return tomorrowMorning
    }

    const rounded = new Date(min.getTime())
    if (Number.isNaN(rounded.getTime())) return new Date()
    rounded.setMinutes(this.roundMinute(rounded.getMinutes() + MINUTE_STEP), 0, 0)
    return this.clamp(rounded)
  }

  daySelectable(date) {
    return this.endOfDay(date) >= this.minDate() && this.startOfDay(date) <= this.maxDate()
  }

  clamp(date) {
    const min = this.minDate()
    const max = this.maxDate()
    if (date < min) return new Date(min.getTime())
    if (date > max) return new Date(max.getTime())
    return date
  }

  minDate() {
    return this.parseLocal(this.minValue) || new Date()
  }

  maxDate() {
    const parsed = this.parseLocal(this.maxValue)
    if (parsed) return parsed
    const fallback = new Date()
    fallback.setDate(fallback.getDate() + 30)
    return fallback
  }

  parseLocal(value) {
    if (!value || typeof value !== "string" || !value.includes("-")) return null
    const [datePart, timePart = "00:00"] = value.split("T")
    const [year, month, day] = datePart.split("-").map(Number)
    const [hour, minute] = timePart.split(":").map(Number)
    if (!year || !month || !day) return null
    const date = new Date(year, month - 1, day, hour || 0, minute || 0, 0, 0)
    return Number.isNaN(date.getTime()) ? null : date
  }

  formatDate(date) {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0")
    ].join("-")
  }

  formatDateTime(date) {
    return `${this.formatDate(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
  }

  startOfMonth(date) {
    if (!date || Number.isNaN(date.getTime())) {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), 1)
    }
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  endOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)
  }

  startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  endOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
  }

  sameDay(left, right) {
    if (!left || !right || Number.isNaN(left.getTime()) || Number.isNaN(right.getTime())) return false
    return left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
  }

  roundMinute(minute) {
    return Math.min(55, Math.round(minute / MINUTE_STEP) * MINUTE_STEP)
  }
}
